import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { Sentry, trackPageview } from "./utils/monitoring";
import { recordFirstTouch } from "./utils/firstTouch";
import { LoginPage } from "./components/features/Auth/LoginPage";
import { MarketingConsentBanner } from "./components/features/Auth/MarketingConsentBanner";
import { HistoryPage } from "./components/features/History/HistoryPage";
import { AdminPage } from "./components/features/Admin/AdminPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PostDeletionNotice } from "./components/features/Account/PostDeletionNotice";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ExamCategoryProvider } from "./contexts/ExamCategoryContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { AppShell } from "./components/layout/AppShell";
import { Loader2 } from "lucide-react";

// Lazy-load pages for code splitting
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage }))
);
const PlanSelectionPage = lazy(() =>
  import("./pages/PlanSelectionPage").then((m) => ({ default: m.PlanSelectionPage }))
);
const GuidePage = lazy(() =>
  import("./pages/GuidePage").then((m) => ({ default: m.GuidePage }))
);
const LearnPage = lazy(() =>
  import("./pages/LearnPage").then((m) => ({ default: m.LearnPage }))
);
const ExamsPage = lazy(() =>
  import("./pages/ExamsPage").then((m) => ({ default: m.ExamsPage }))
);
const MockPage = lazy(() =>
  import("./pages/MockPage").then((m) => ({ default: m.MockPage }))
);
const MyPage = lazy(() =>
  import("./pages/MyPage").then((m) => ({ default: m.MyPage }))
);
const QuizPage = lazy(() =>
  import("./pages/QuizPage").then((m) => ({ default: m.QuizPage }))
);
const AudioPlayerPage = lazy(() =>
  import("./pages/AudioPlayerPage").then((m) => ({ default: m.AudioPlayerPage }))
);
const TryQuizPage = lazy(() =>
  import("./pages/TryQuizPage").then((m) => ({ default: m.TryQuizPage }))
);
const TryLecturePage = lazy(() =>
  import("./pages/TryLecturePage").then((m) => ({ default: m.TryLecturePage }))
);
const UpgradePage = lazy(() =>
  import("./pages/UpgradePage").then((m) => ({ default: m.UpgradePage }))
);
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));

/**
 * OAuthコールバック(?code=)のクエリパラメータを保持しつつリダイレクト
 */
function DefaultRedirect() {
  const search = window.location.search;
  if (search.includes("code=")) {
    // OAuth callback: AuthGuard内のLoginPage→AuthProviderでcode処理
    return <Navigate to={`/guide${search}`} replace />;
  }
  return <Navigate to="/guide" replace />;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
    </div>
  );
}

/**
 * 認証ガード: 未認証→ログイン、認証済み→子ルート
 */
function AuthGuard() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // オンボーディング（プラン選択）から来た場合は新規登録から始める
    const signup = (location.state as { signup?: boolean } | null)?.signup === true;
    return <LoginPage initialMode={signup ? "register" : "login"} />;
  }

  return (
    <>
      <MarketingConsentBanner />
      <Outlet />
    </>
  );
}

/**
 * ルート変更ごとに GA4 へページビューを送信（測定ID未設定なら no-op）
 */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    recordFirstTouch(location.search);
    trackPageview(location.pathname, location.search);
    // search は意図的に依存へ含めない（ページビューはパス変更時のみ送る）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
}

/**
 * 予期しない描画エラー時のフォールバック画面（Sentry ErrorBoundary 用）
 */
function ErrorFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-slate-50 dark:bg-slate-900">
      <p className="text-lg font-bold text-slate-900 dark:text-white">
        問題が発生しました
      </p>
      <p className="text-sm text-slate-500">
        再読み込みしても直らない場合は、時間をおいてお試しください。
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-3 rounded-xl bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors"
      >
        再読み込み
      </button>
    </div>
  );
}

/**
 * メインアプリケーション
 * 認証済みページは全て AppShell（AppHeader + BottomNav）配下に統一
 */
function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <ThemeProvider>
      <AuthProvider>
        <UserProfileProvider>
        <ExamCategoryProvider>
        <BrowserRouter>
          <PostDeletionNotice />
          <PageViewTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* 認証不要: オンボーディング */}
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/onboarding/plan" element={<PlanSelectionPage />} />

              {/* 認証不要: 法務ページ */}
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />

              {/* 認証不要: サポート（ストアのサポートURL・未ログインで到達する） */}
              <Route path="/support" element={<SupportPage />} />

              {/* 認証不要: ガイドページ（未認証でも閲覧可能） */}
              <Route element={<AppShell />}>
                <Route path="/guide" element={<GuidePage />} />
                {/* 認証不要: お試し（登録なしの入口。docs/service-design.md） */}
                <Route path="/try" element={<TryQuizPage />} />
                <Route path="/try/lecture/:exam/:themeId" element={<TryLecturePage />} />
              </Route>

              {/* 認証必須 */}
              <Route element={<AuthGuard />}>
                {/* AppShell (AppHeader + BottomNav) */}
                <Route element={<AppShell />}>
                  <Route path="/learn" element={<LearnPage />} />
                  <Route path="/learn/:themeId" element={<AudioPlayerPage />} />
                  <Route path="/exams" element={<ExamsPage />} />
                  <Route path="/exams/quiz" element={<QuizPage />} />
                  <Route path="/mock" element={<MockPage />} />
                  <Route path="/mypage" element={<MyPage />} />
                  <Route path="/upgrade" element={<UpgradePage />} />
                  <Route path="/history" element={<HistoryPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                </Route>
              </Route>

              {/* デフォルト: OAuthコールバック(?code=)はクエリを保持、それ以外はガイドへ */}
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </ExamCategoryProvider>
        </UserProfileProvider>
      </AuthProvider>
    </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
